(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.SpinweldCart = api;
})(typeof window === 'object' ? window : globalThis, function () {
  'use strict';
  const groupOf = item => item.properties?.['_Spinweld Job ID'];
  const identity = item => JSON.stringify([String(item.variant_id || item.id), Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b))]);
  function groupItems(cart, group) { return cart.items.filter(item => groupOf(item) === group); }
  function matches(cart, group, expected) {
    const sums = items => { const result = new Map(); items.forEach(item => { const key = identity(item); result.set(key, (result.get(key) || 0) + item.quantity); }); return result; };
    const actual = sums(groupItems(cart, group)), wanted = sums(expected);
    return actual.size === wanted.size && [...wanted].every(([key, qty]) => actual.get(key) === qty);
  }
  function quantityUpdates(cart, group, quantity) {
    if (!Number.isInteger(quantity) || quantity < 0 || quantity > 20) throw new Error('Choose a quantity from 0 to 20.');
    const lines = groupItems(cart, group);
    if (!lines.length) throw new Error('This tank is no longer in the cart.');
    return Object.fromEntries(lines.map(item => {
      const units = Number(item.properties['_Spinweld Units Per Tank']);
      if (!Number.isInteger(units) || units < 1 || units > 80) throw new Error('This older build needs to be reopened in the builder before changing its quantity.');
      return [item.key, units * quantity];
    }));
  }
  function quantityMatches(before, after, group, quantity) {
    if (quantity === 0) return groupItems(after, group).length === 0;
    const expected = groupItems(before, group).map(item => ({ ...item, quantity: Number(item.properties['_Spinweld Units Per Tank']) * quantity }));
    return expected.length > 0 && matches(after, group, expected) && !invalidGroups(after).includes(group);
  }
  function noteForCart(cart) {
    const original = String(cart.note || '').replace(/\n?\[ERVP TANK BUILDS\][\s\S]*?\[\/ERVP TANK BUILDS\]\n?/g, '').trim();
    const tanks = cart.items.filter(item => groupOf(item) && item.properties['_Spinweld Component'] === 'tank');
    const blocks = tanks.map(item => {
      const group = groupItems(cart, groupOf(item));
      const installed = group.some(line => line.properties['_Spinweld Component'] === 'installation');
      return `${installed ? 'SPINWELD INSTALLATION ORDER' : 'BLANK TANK — NO INSTALLATION'}\nJob ID: ${groupOf(item)}\nQuantity: ${item.quantity}\nInstallation PDF: ${item.properties['View Installation PDF'] || ''}`;
    });
    return [original, blocks.length ? `[ERVP TANK BUILDS]\n${blocks.join('\n\n')}\n[/ERVP TANK BUILDS]` : ''].filter(Boolean).join('\n\n');
  }
  function invalidGroups(cart) {
    const groups = [...new Set(cart.items.map(groupOf).filter(Boolean))];
    return groups.filter(group => {
      const items = groupItems(cart, group);
      const count = Number(items[0].properties['_Spinweld Fitting Count']);
      if (!Number.isInteger(count) || count < 0 || count > 80) return false; // legacy group, cannot infer its contract
      const tanks = items.filter(item => item.properties['_Spinweld Component'] === 'tank');
      if (tanks.length !== 1) return true;
      const qty = tanks[0].quantity;
      if (!Number.isInteger(qty) || qty < 1 || qty > 20) return true;
      const sum = role => items.filter(item => item.properties['_Spinweld Component'] === role).reduce((total,item) => total + item.quantity,0);
      return items.some(item => Number(item.properties['_Spinweld Fitting Count']) !== count || !['tank','fitting','installation'].includes(item.properties['_Spinweld Component']) || !Number.isInteger(item.quantity) || item.quantity < 1 || Number(item.properties['_Spinweld Units Per Tank']) * qty !== item.quantity) || sum('fitting') !== qty * count || sum('installation') !== qty * count;
    });
  }
  function createClient({ request, storage }) {
    const journalKey = 'ervp.spinweld.cart.pending.v1';
    let busy = false;
    async function synchronizeNote(cart) { return request('cart/update.js', { note: noteForCart(cart) }); }
    async function commit(items, group, replaceGroup) {
      if (busy) throw new Error('A tank cart update is already in progress.');
      busy = true;
      try {
        const pending = JSON.parse(storage.getItem(journalKey) || 'null');
        // An uncertain write must be reconciled, never blindly added a second time.
        if (pending) {
          let cart = await request('cart.js');
          if (!matches(cart, pending.group, pending.items)) throw new Error('A previous cart update is incomplete. Review your cart before trying again; no additional items were added.');
          if (pending.replaceGroup && groupItems(cart, pending.replaceGroup).length) {
            cart = await request('cart/update.js', { updates: Object.fromEntries(groupItems(cart, pending.replaceGroup).map(item => [item.key, 0])) });
          }
          if (!matches(cart, pending.group, pending.items) || (pending.replaceGroup && groupItems(cart, pending.replaceGroup).length)) throw new Error('The previous cart update still needs review.');
          await synchronizeNote(cart); storage.removeItem(journalKey); return { recovered: true };
        }
        const before = await request('cart.js');
        if (replaceGroup && !groupItems(before, replaceGroup).length) throw new Error('The original tank is no longer in the cart. Open the cart and start a new build.');
        storage.setItem(journalKey, JSON.stringify({ items, group, replaceGroup }));
        if (!matches(before, group, items)) await request('cart/add.js', { items });
        let after = await request('cart.js');
        if (!matches(after, group, items)) throw new Error('Shopify did not add the complete tank, fittings and installation. The original build has not been removed. Review your cart.');
        if (replaceGroup) after = await request('cart/update.js', { updates: Object.fromEntries(groupItems(after, replaceGroup).map(item => [item.key, 0])) });
        if (!matches(after, group, items) || (replaceGroup && groupItems(after, replaceGroup).length)) throw new Error('The cart update could not be verified. Review your cart before ordering.');
        await synchronizeNote(after); storage.removeItem(journalKey); return { recovered: false };
      } finally { busy = false; }
    }
    return { commit, synchronizeNote };
  }
  return { groupOf, groupItems, matches, quantityUpdates, quantityMatches, noteForCart, invalidGroups, createClient };
});
